import { Component, OnInit, inject, OnDestroy, Input, ChangeDetectorRef } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormGroupDirective,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { Store } from "@ngrx/store";
import { Subject, takeUntil, filter, take } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import {
  selectActivePacket,
  selectIsCehrtValidating,
  selectIsCehrtValid,
  selectEffectiveQuestionnaire,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { MipsSettings } from "@models/mips-stepper.model";
import { MipsValidationService } from "@services/mips-validation.service";
import { getCurrentPerformanceYear } from "../../constants/mips-constants";

@Component({
  selector: "app-mips-settings",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    MatSnackBarModule,
  ],
  templateUrl: "./mips-settings.component.html",
  styleUrls: ["./mips-settings.component.css"],
})
export class MipsSettingsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly validationService = inject(MipsValidationService);
  private readonly destroy$ = new Subject<void>();

  currentPacket = toSignal(this.store.select(selectActivePacket));
  isCehrtValidating = toSignal(this.store.select(selectIsCehrtValidating));
  isCehrtValid = toSignal(this.store.select(selectIsCehrtValid));
  questionnaire = toSignal(this.store.select(selectEffectiveQuestionnaire));

  get isPIRequired(): boolean {
    const excluded = this.validationService.getExcludedCategories(this.questionnaire());
    return !excluded.has('pi');
  }

  settingsForm!: FormGroup;
  private formInitialized = false;
  private lastLoadedPacketId: number | null = null;

  get performanceYear(): number {
    return this.currentPacket()?.performanceYear || getCurrentPerformanceYear();
  }

  get tin(): string {
    const tinFromPacket = this.currentPacket()?.tinDetails?.tin;
    return tinFromPacket || "N/A";
  }

  get hasTin(): boolean {
    return !!this.currentPacket()?.tinDetails?.tin;
  }

  get showValidationSuccess(): boolean {
    return this.isCehrtValid() === true;
  }

  get showValidationWarning(): boolean {
    return this.isCehrtValid() === false;
  }

  ngOnInit(): void {
    this.settingsForm = this.fb.group(
      {
        cehrtId: [""],
        confirmCehrtId: [""],
      },
      {
        validators: this.cehrtMatchValidator(),
      }
    );

    this.parentForm = this.ctrlContainer.form;

    this.store
      .select(selectActivePacket)
      .pipe(takeUntil(this.destroy$))
      .subscribe((packet) => {
        const newPacketId = packet?.id ?? null;
        if (newPacketId !== this.lastLoadedPacketId) {
          this.formInitialized = false;
          this.lastLoadedPacketId = newPacketId;
        }

        if (!packet) {
          this.formInitialized = false;
          this.settingsForm.patchValue(
            {
              cehrtId: "",
              confirmCehrtId: "",
            },
            { emitEvent: false },
          );
          this.store.dispatch(MipsPacketActions.resetCEHRTValidation());
          this.updateParentFormValidity();
          return;
        }

        if (!this.formInitialized && packet.settings) {
          this.loadFromPacket();
        }

        this.updateParentFormValidity();
      });

    this.settingsForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateParentFormValidity();
      });

    setTimeout(() => {
      this.updateParentFormValidity();
    });

    ['cehrtId', 'confirmCehrtId'].forEach(field => {
      this.settingsForm.get(field)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.formInitialized) {
            this.store.dispatch(MipsPacketActions.resetCEHRTValidation());
          }
        });
    });
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.setErrors(null);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFromPacket(): void {
    if (this.formInitialized) return;

    const settings = this.currentPacket()?.settings;
    if (!settings) return;

    this.formInitialized = true;
    this.lastLoadedPacketId = this.currentPacket()?.id ?? null;

    this.settingsForm.patchValue(
      {
        cehrtId: settings.cehrtId,
        confirmCehrtId: settings.confirmCehrtId,
      },
      { emitEvent: false }
    );
  }

  validateCehrtId(): void {
    const cehrtId = this.settingsForm.get("cehrtId")?.value;
    const confirmCehrtId = this.settingsForm.get("confirmCehrtId")?.value;

    if (!cehrtId || !confirmCehrtId) {
      this.snackBar.open("Please enter CEHRT ID in both fields", "Close", {
        duration: 3000,
      });
      return;
    }

    if (cehrtId !== confirmCehrtId) {
      this.snackBar.open("CEHRT IDs do not match", "Close", {
        duration: 3000,
      });
      return;
    }
    this.store.dispatch(MipsPacketActions.validateCEHRTID({ cehrtId }));

    this.store
      .select(selectIsCehrtValid)
      .pipe(
        filter((isValid) => isValid !== null),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((isValid) => {
        if (isValid) {
          this.snackBar.open("CEHRT ID validated successfully!", "Close", {
            duration: 3000,
          });

          const packet = this.currentPacket();
          if (packet?.id) {
            this.saveSettings();
          }
        } else {
          this.snackBar.open(
            "Invalid CEHRT ID. Please verify and try again.",
            "Close",
            {
              duration: 4000,
            }
          );
        }
      });
  }

  private saveSettings(): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    const formValue = this.settingsForm.getRawValue();

    const settings: MipsSettings = {
      ...(packet.settings || {}),
      cehrtId: formValue.cehrtId,
      confirmCehrtId: formValue.confirmCehrtId,
    } as MipsSettings;

    this.store.dispatch(
      MipsPacketActions.saveSettings({
        settings,
      })
    );
  }

  get canProceedToNextStep(): boolean {
    return this.settingsForm.valid;
  }

  private updateParentFormValidity(): void {
    if (this.parentForm) {
      if (this.canProceedToNextStep) {
        this.parentForm.setErrors(null);
      } else {
        this.parentForm.setErrors({ incomplete: true });
      }
      this.cdr.markForCheck();
    }
  }

  cehrtMatchValidator() {
    return (formGroup: FormGroup) => {
      const cehrtId = formGroup.get("cehrtId");
      const confirmCehrtId = formGroup.get("confirmCehrtId");

      if (cehrtId && confirmCehrtId && (cehrtId.value || confirmCehrtId.value) && cehrtId.value !== confirmCehrtId.value) {
        confirmCehrtId.setErrors({ cehrtMismatch: true });
      } else {
        if (confirmCehrtId?.hasError("cehrtMismatch")) {
          confirmCehrtId.setErrors(null);
        }
      }
      return null;
    };
  }

  generateCertId(): void {
    window.open("https://chpl.healthit.gov/#/search", "_blank");
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.settingsForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  get reportingLevel(): string {
    return this.currentPacket()?.reportingLevel || "group";
  }

  navigateToEligibilityStep(): void {
    this.store.dispatch(MipsPacketActions.navigateToStep({ stepIndex: 1 }));
  }
}
